import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router";
import * as Dialog from "@radix-ui/react-dialog";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import Button from "@/components/ui/button";
import type { Review, ReviewFilters, ReviewSource, ReviewType, ReviewContentType, ReviewRatingFilter, ReviewTagsMode, ReviewAvatarFilter, ReviewCompanyLogoFilter } from "@/modules/review/types";
import type { Tag } from "@/modules/tag/types";
import type { UserRole } from "@/modules/membership";
import { getTagColorStyles } from "@/modules/tag/colors";
import { useReviews } from "@/modules/review/hooks/use-reviews";
import PinnedReviewItem from "./pinned-review-item";
import ReviewSelectItem from "./review-select-item";
import IconPin from "@/components/icons/icon-pin";

// Filter options
const SOURCE_OPTIONS: { value: ReviewSource; label: string }[] = [
	{ value: "form", label: "Form" },
	{ value: "manual", label: "Manual" },
	{ value: "import", label: "Import" },
	{ value: "google_reviews", label: "Google Reviews" },
	{ value: "twitter", label: "Twitter" },
	{ value: "linkedin", label: "LinkedIn" },
	{ value: "facebook", label: "Facebook" },
	{ value: "instagram", label: "Instagram" },
	{ value: "capterra", label: "Capterra" },
	{ value: "trustpilot", label: "Trustpilot" },
	{ value: "email", label: "Email" },
	{ value: "slack", label: "Slack" },
	{ value: "product-hunt", label: "Product Hunt" },
	{ value: "other", label: "Other" },
];

const TYPE_OPTIONS: { value: ReviewType | ""; label: string }[] = [
	{ value: "", label: "All" },
	{ value: "testimonial", label: "Testimonial" },
	{ value: "feedback", label: "Feedback" },
];

const CONTENT_TYPE_OPTIONS: { value: ReviewContentType; label: string }[] = [
	{ value: "text", label: "Text" },
	{ value: "video", label: "Video" },
	{ value: "audio", label: "Audio" },
];

const RATING_OPTIONS: { value: ReviewRatingFilter | ""; label: string }[] = [
	{ value: "", label: "All Ratings" },
	{ value: "5_and_above", label: "5 Stars Only" },
	{ value: "4_and_above", label: "4+ Stars" },
	{ value: "3_and_above", label: "3+ Stars" },
	{ value: "2_and_above", label: "2+ Stars" },
];

const PROFILE_OPTIONS: { value: ReviewAvatarFilter | ""; label: string }[] = [
	{ value: "", label: "All" },
	{ value: "with_avatar", label: "With Photo" },
	{ value: "without_avatar", label: "Without Photo" },
];

const COMPANY_OPTIONS: { value: ReviewCompanyLogoFilter | ""; label: string }[] = [
	{ value: "", label: "All" },
	{ value: "with_logo", label: "With Logo" },
	{ value: "without_logo", label: "Without Logo" },
];

const DATE_PRESET_OPTIONS = [
	{ value: "", label: "All Time" },
	{ value: "today", label: "Today" },
	{ value: "last_7_days", label: "Last 7 Days" },
	{ value: "last_30_days", label: "Last 30 Days" },
	{ value: "this_month", label: "This Month" },
	{ value: "this_year", label: "This Year" },
];

function getDateFilterValue(preset: string): string | undefined {
	const today = new Date();
	const formatDate = (date: Date) => date.toISOString().split("T")[0];

	switch (preset) {
		case "today":
			return `between:${formatDate(today)},${formatDate(today)}`;
		case "last_7_days": {
			const sevenDaysAgo = new Date(today);
			sevenDaysAgo.setDate(today.getDate() - 7);
			return `after:${formatDate(sevenDaysAgo)}`;
		}
		case "last_30_days": {
			const thirtyDaysAgo = new Date(today);
			thirtyDaysAgo.setDate(today.getDate() - 30);
			return `after:${formatDate(thirtyDaysAgo)}`;
		}
		case "this_month": {
			const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
			return `between:${formatDate(startOfMonth)},${formatDate(today)}`;
		}
		case "this_year": {
			const startOfYear = new Date(today.getFullYear(), 0, 1);
			return `between:${formatDate(startOfYear)},${formatDate(today)}`;
		}
		default:
			return undefined;
	}
}


// Section Icons
const SectionIcons = {
	type: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={1.5}
				d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
			/>
		</svg>
	),
	format: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={1.5}
				d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
			/>
		</svg>
	),
	source: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={1.5}
				d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
			/>
		</svg>
	),
	rating: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={1.5}
				d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
			/>
		</svg>
	),
	date: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
		</svg>
	),
	profile: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
		</svg>
	),
	company: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m1-4h1m1-4h1M21 21a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
		</svg>
	),
	tags: (
		<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth={1.5}
				d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
			/>
		</svg>
	),
};

// Accordion Section Component
function FilterSection({
	title,
	icon,
	isOpen,
	isActive,
	onToggle,
	children,
}: {
	title: string;
	icon: React.ReactNode;
	isOpen: boolean;
	isActive: boolean;
	onToggle: () => void;
	children: React.ReactNode;
}) {
	return (
		<div className="border-b border-gray-200 last:border-b-0">
			<button type="button" onClick={onToggle} className="flex items-center justify-between w-full px-0 py-3 text-left hover:opacity-80 transition-opacity">
				<div className="flex items-center gap-2.5">
					<span className="text-gray-400">{icon}</span>
					<span className="text-sm font-medium text-gray-900">{title}</span>
				</div>
				<div className="flex items-center gap-2">
					{isActive && <span className="w-2 h-2 rounded-full bg-gray-800" />}
					<svg className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
					</svg>
				</div>
			</button>
			{isOpen && <div className="pb-4">{children}</div>}
		</div>
	);
}

// Chip/Pill Option Component
function ChipOption({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: React.ReactNode }) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selected ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
		>
			{children}
		</button>
	);
}

// Helper to check if value is selected in a multi-select filter
function isSelected<T extends string>(current: T | T[] | undefined, value: T): boolean {
	if (!current) return false;
	if (Array.isArray(current)) return current.includes(value);
	return current === value;
}

interface SelectPinnedReviewsModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	workspaceId: string;
	wallId: string | null;
	dynamicWallFilters: ReviewFilters | null;
	initialPinnedIds: string[];
	tags: Tag[];
	maxPinnedReviews?: number | null;
	userRole?: UserRole;
	onSave: (pinnedIds: string[], totalMatchingCount: number) => void;
}

export default function SelectPinnedReviewsModal({
	open,
	onOpenChange,
	workspaceId,
	wallId,
	dynamicWallFilters,
	initialPinnedIds,
	tags,
	onSave,
	maxPinnedReviews,
	userRole,
}: SelectPinnedReviewsModalProps) {
	const navigate = useNavigate();
	// Local state for the modal
	const [localPinnedIds, setLocalPinnedIds] = useState<string[]>(initialPinnedIds);
	const [pinnedReviewsData, setPinnedReviewsData] = useState<Review[]>([]);
	// Always start with no filters applied (only approved status)
	const [localFilters, setLocalFilters] = useState<ReviewFilters>({ status: ["approved"] });
	const [datePreset, setDatePreset] = useState("");
	const [openSections, setOpenSections] = useState<Set<string>>(new Set(["type"]));

	// Keep a ref of pinned IDs so loadReviews doesn't re-trigger on pin/unpin
	const localPinnedIdsRef = useRef(localPinnedIds);
	localPinnedIdsRef.current = localPinnedIds;

	// Reviews state
	const { indexReviewsForWallOfLove, getReviewsByIds } = useReviews();
	const [allReviews, setAllReviews] = useState<Review[]>([]);
	const [, setTotalReviewCount] = useState<number>(0);
	const [isLoadingReviews, setIsLoadingReviews] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [nextCursor, setNextCursor] = useState<string | null>(null);

	// Drag and drop sensors
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	// Reset state when modal opens and load pinned reviews from API
	useEffect(() => {
		if (open) {
			setLocalPinnedIds(initialPinnedIds);
			setPinnedReviewsData([]);
			setDatePreset("");

			// Load all pinned reviews from the API
			if (workspaceId && initialPinnedIds.length > 0) {
				getReviewsByIds(workspaceId, initialPinnedIds).then((fetched) => {
					if (fetched && fetched.length > 0) {
						setPinnedReviewsData(fetched);
					}
				});
			}
		}
	}, [open, workspaceId, initialPinnedIds, getReviewsByIds]);

	// Load reviews when modal opens or filters change
	const loadReviews = useCallback(
		async (cursor?: string | null) => {
			if (!workspaceId) return;

			if (cursor) {
				setIsLoadingMore(true);
			} else {
				setIsLoadingReviews(true);
			}

			try {
				if (!wallId) {
					return;
				}
				const response = await indexReviewsForWallOfLove(workspaceId, cursor, localFilters, undefined, localPinnedIdsRef.current);
				if (response) {
					if (cursor) {
						setAllReviews((prev) => [...prev, ...response.data]);
					} else {
						setAllReviews(response.data);
						setTotalReviewCount(response.meta.total);
					}
					setNextCursor(response.meta.next_cursor);
				}
			} finally {
				setIsLoadingReviews(false);
				setIsLoadingMore(false);
			}
		},
		[workspaceId, indexReviewsForWallOfLove, localFilters, wallId]
	);

	// Load reviews when modal opens
	useEffect(() => {
		if (open) {
			loadReviews();
		}
	}, [open, loadReviews]);

	// Compute pinned and matching reviews
	const pinnedReviews = useMemo(() => {
		// Map pinned IDs to reviews, first checking pinnedReviewsData, then allReviews
		return localPinnedIds
			.map((id) => {
				// First try to find in pinnedReviewsData (reviews passed from parent)
				const pinnedReview = pinnedReviewsData.find((r) => r.id === id);
				if (pinnedReview) return pinnedReview;
				// Fall back to allReviews (for newly pinned reviews from filtered list)
				return allReviews.find((r) => r.id === id);
			})
			.filter((r): r is Review => r !== undefined);
	}, [localPinnedIds, pinnedReviewsData, allReviews]);

	const matchingReviews = useMemo(() => {
		return allReviews.filter((r) => !localPinnedIds.includes(r.id));
	}, [allReviews, localPinnedIds]);

	// Filter handlers
	const toggleSection = (section: string) => {
		setOpenSections((prev) => {
			const next = new Set(prev);
			if (next.has(section)) {
				next.delete(section);
			} else {
				next.add(section);
			}
			return next;
		});
	};

	const handleFilterChange = <K extends keyof ReviewFilters>(key: K, value: ReviewFilters[K] | "") => {
		const newFilters = { ...localFilters };
		if (value === "" || value === undefined) {
			delete newFilters[key];
		} else {
			newFilters[key] = value;
		}
		setLocalFilters(newFilters);
	};

	const handleDatePresetChange = (preset: string) => {
		setDatePreset(preset);
		const dateValue = getDateFilterValue(preset);
		handleFilterChange("date", dateValue);
	};

	const handleTagToggle = (tagId: string) => {
		const currentTags = localFilters.tags || [];
		const newTags = currentTags.includes(tagId) ? currentTags.filter((id) => id !== tagId) : [...currentTags, tagId];
		handleFilterChange("tags", newTags.length > 0 ? newTags : undefined);
	};

	const handleTagsModeChange = (mode: ReviewTagsMode) => {
		handleFilterChange("tags_mode", mode);
	};

	const handleSourceToggle = (value: ReviewSource) => {
		const current = localFilters.source;
		const currentArray = Array.isArray(current) ? current : current ? [current] : [];
		const newArray = currentArray.includes(value) ? currentArray.filter((v) => v !== value) : [...currentArray, value];
		handleFilterChange("source", newArray.length > 0 ? (newArray.length === 1 ? newArray[0] : newArray) : undefined);
	};

	const handleContentTypeToggle = (value: ReviewContentType) => {
		const current = localFilters.content_type;
		const currentArray = Array.isArray(current) ? current : current ? [current] : [];
		const newArray = currentArray.includes(value) ? currentArray.filter((v) => v !== value) : [...currentArray, value];
		handleFilterChange("content_type", newArray.length > 0 ? (newArray.length === 1 ? newArray[0] : newArray) : undefined);
	};

	const handleProfileToggle = (value: ReviewAvatarFilter) => {
		handleFilterChange("customer_avatar_url", value);
	};

	const handleCompanyToggle = (value: ReviewCompanyLogoFilter) => {
		handleFilterChange("customer_company_logo_url", value);
	};

	// Pin/unpin handlers
	const handlePin = (reviewId: string) => {
		if (maxPinnedReviews !== null && maxPinnedReviews !== undefined && localPinnedIds.length >= maxPinnedReviews) return;
		setLocalPinnedIds((prev) => [...prev, reviewId]);
		// Add the review to pinnedReviewsData if it's from allReviews
		const review = allReviews.find((r) => r.id === reviewId);
		if (review && !pinnedReviewsData.some((r) => r.id === reviewId)) {
			setPinnedReviewsData((prev) => [...prev, review]);
		}
	};

	const handleUnpin = (reviewId: string) => {
		setLocalPinnedIds((prev) => prev.filter((id) => id !== reviewId));
	};

	// Drag end handler
	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (over && active.id !== over.id) {
			setLocalPinnedIds((ids) => {
				const oldIndex = ids.indexOf(active.id as string);
				const newIndex = ids.indexOf(over.id as string);
				return arrayMove(ids, oldIndex, newIndex);
			});
		}
	};

	// Save handler
	const handleSave = async () => {
		if (!workspaceId || !wallId) {
			onSave(localPinnedIds, 0);
			onOpenChange(false);
			return;
		}

		onOpenChange(false);

		try {
			// Load reviews with current filters to get total count
			const response = await indexReviewsForWallOfLove(workspaceId, null, dynamicWallFilters ?? undefined, undefined, localPinnedIds);
			if (response) {
				const totalReviewCount = response.meta.total;
				onSave(localPinnedIds, totalReviewCount);
			} else {
				onSave(localPinnedIds, 0);
			}
		} catch (error) {
			// On error, still save but with 0 count
			onSave(localPinnedIds, 0);
		}
	};

	// Load more handler
	const handleLoadMore = () => {
		if (nextCursor && !isLoadingMore) {
			loadReviews(nextCursor);
		}
	};

	const isAtMaxPinned = maxPinnedReviews !== null && maxPinnedReviews !== undefined && localPinnedIds.length >= maxPinnedReviews;

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 modal-overlay" />
				<Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-6xl h-[85vh] flex flex-col modal-content overflow-hidden">
					{/* Header */}
					<div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
						<div>
							<Dialog.Title className="text-lg font-semibold text-gray-900">Pinned Reviews</Dialog.Title>
							<Dialog.Description className="text-sm text-tertiary">
								Pin reviews to always show them on top, regardless of filters.
							</Dialog.Description>
						</div>
						<Dialog.Close asChild>
							<button className="text-gray-400 hover:text-gray-600 transition-colors p-1" aria-label="Close">
								<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
								</svg>
							</button>
						</Dialog.Close>
					</div>

					{/* Three-column body */}
					<div className="flex-1 flex overflow-hidden">
						{/* Left column: Filters */}
						<div className="w-72 border-r border-gray-200 p-6 overflow-y-auto shrink-0">
							<h3 className="font-semibold text-gray-900 mb-4">Filters</h3>

							{/* Type */}
							<FilterSection title="Type" icon={SectionIcons.type} isOpen={openSections.has("type")} isActive={!!localFilters.type} onToggle={() => toggleSection("type")}>
								<div className="flex flex-wrap gap-2">
									{TYPE_OPTIONS.map((option) => (
										<ChipOption
											key={option.value}
											selected={localFilters.type === option.value || (!localFilters.type && option.value === "")}
											onClick={() => handleFilterChange("type", option.value as ReviewType)}
										>
											{option.label}
										</ChipOption>
									))}
								</div>
							</FilterSection>

							{/* Format */}
							<FilterSection
								title="Format"
								icon={SectionIcons.format}
								isOpen={openSections.has("content_type")}
								isActive={Array.isArray(localFilters.content_type) ? localFilters.content_type.length > 0 : !!localFilters.content_type}
								onToggle={() => toggleSection("content_type")}
							>
								<div className="flex flex-wrap gap-2">
									<ChipOption
										selected={!localFilters.content_type || (Array.isArray(localFilters.content_type) && localFilters.content_type.length === 0)}
										onClick={() => handleFilterChange("content_type", undefined)}
									>
										All
									</ChipOption>
									{CONTENT_TYPE_OPTIONS.map((option) => (
										<ChipOption key={option.value} selected={isSelected(localFilters.content_type, option.value)} onClick={() => handleContentTypeToggle(option.value)}>
											{option.label}
										</ChipOption>
									))}
								</div>
							</FilterSection>

							{/* Source */}
							<FilterSection
								title="Source"
								icon={SectionIcons.source}
								isOpen={openSections.has("source")}
								isActive={Array.isArray(localFilters.source) ? localFilters.source.length > 0 : !!localFilters.source}
								onToggle={() => toggleSection("source")}
							>
								<div className="flex flex-wrap gap-2">
									<ChipOption
										selected={!localFilters.source || (Array.isArray(localFilters.source) && localFilters.source.length === 0)}
										onClick={() => handleFilterChange("source", undefined)}
									>
										All
									</ChipOption>
									{SOURCE_OPTIONS.map((option) => (
										<ChipOption key={option.value} selected={isSelected(localFilters.source, option.value)} onClick={() => handleSourceToggle(option.value)}>
											{option.label}
										</ChipOption>
									))}
								</div>
							</FilterSection>

							{/* Rating */}
							<FilterSection title="Rating" icon={SectionIcons.rating} isOpen={openSections.has("rating")} isActive={!!localFilters.rating} onToggle={() => toggleSection("rating")}>
								<div className="flex flex-wrap gap-2">
									{RATING_OPTIONS.map((option) => (
										<ChipOption
											key={option.value}
											selected={localFilters.rating === option.value || (!localFilters.rating && option.value === "")}
											onClick={() => handleFilterChange("rating", option.value as ReviewRatingFilter)}
										>
											{option.label}
										</ChipOption>
									))}
								</div>
							</FilterSection>

							{/* Date */}
							<FilterSection title="Date" icon={SectionIcons.date} isOpen={openSections.has("date")} isActive={!!localFilters.date} onToggle={() => toggleSection("date")}>
								<div className="flex flex-wrap gap-2">
									{DATE_PRESET_OPTIONS.map((option) => (
										<ChipOption key={option.value} selected={datePreset === option.value} onClick={() => handleDatePresetChange(option.value)}>
											{option.label}
										</ChipOption>
									))}
								</div>
							</FilterSection>

							{/* Profile */}
							<FilterSection title="Profile" icon={SectionIcons.profile} isOpen={openSections.has("profile")} isActive={!!localFilters.customer_avatar_url} onToggle={() => toggleSection("profile")}>
								<div className="flex flex-wrap gap-2">
									{PROFILE_OPTIONS.map((option) => (
										<ChipOption
											key={option.value}
											selected={localFilters.customer_avatar_url === option.value || (!localFilters.customer_avatar_url && option.value === "")}
											onClick={() => handleProfileToggle(option.value as ReviewAvatarFilter)}
										>
											{option.label}
										</ChipOption>
									))}
								</div>
							</FilterSection>

							{/* Company */}
							<FilterSection title="Company" icon={SectionIcons.company} isOpen={openSections.has("company")} isActive={!!localFilters.customer_company_logo_url} onToggle={() => toggleSection("company")}>
								<div className="flex flex-wrap gap-2">
									{COMPANY_OPTIONS.map((option) => (
										<ChipOption
											key={option.value}
											selected={localFilters.customer_company_logo_url === option.value || (!localFilters.customer_company_logo_url && option.value === "")}
											onClick={() => handleCompanyToggle(option.value as ReviewCompanyLogoFilter)}
										>
											{option.label}
										</ChipOption>
									))}
								</div>
							</FilterSection>

							{/* Tags */}
							{tags.length > 0 && (
								<FilterSection
									title="Tags"
									icon={SectionIcons.tags}
									isOpen={openSections.has("tags")}
									isActive={!!localFilters.tags && localFilters.tags.length > 0}
									onToggle={() => toggleSection("tags")}
								>
									<div className="space-y-3">
										<div className="flex flex-wrap gap-2">
											{tags.map((tag) => {
												const isTagSelected = localFilters.tags?.includes(tag.id) || false;
												const colorStyles = getTagColorStyles(tag.color);
												return (
													<button
														key={tag.id}
														type="button"
														onClick={() => handleTagToggle(tag.id)}
														className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-all ${colorStyles.bg} ${colorStyles.text} ${
															isTagSelected ? "ring-2 ring-offset-1 ring-gray-800" : "opacity-70 hover:opacity-100"
														}`}
													>
														{tag.label}
													</button>
												);
											})}
										</div>
										{localFilters.tags && localFilters.tags.length > 1 && (
											<div className="flex items-center gap-2 pt-2 border-t border-gray-200">
												<span className="text-xs text-tertiary">Match:</span>
												<button
													type="button"
													onClick={() => handleTagsModeChange("any")}
													className={`px-2 py-1 text-xs rounded ${
														localFilters.tags_mode !== "all" ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
													}`}
												>
													Any tag
												</button>
												<button
													type="button"
													onClick={() => handleTagsModeChange("all")}
													className={`px-2 py-1 text-xs rounded ${
														localFilters.tags_mode === "all" ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
													}`}
												>
													All tags
												</button>
											</div>
										)}
									</div>
								</FilterSection>
							)}
						</div>

						{/* Middle column: All Reviews */}
						<div className="flex-1 basis-0 border-r border-gray-200 flex flex-col overflow-hidden">
							<div className="py-3 px-6 border-b border-gray-200 shrink-0">
								<h3 className="font-semibold text-gray-900">All Reviews</h3>
								<p className="text-sm text-tertiary mt-1">Filter and select reviews to pin.</p>
							</div>

							<div className="flex-1 p-6 overflow-y-auto">
								{isLoadingReviews ? (
									<div className="space-y-2">
										{[1, 2, 3, 4].map((i) => (
											<div key={i} className="animate-pulse flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
												<div className="w-10 h-10 bg-gray-200 rounded-full" />
												<div className="flex-1 space-y-2">
													<div className="h-4 bg-gray-200 rounded w-1/3" />
													<div className="h-3 bg-gray-200 rounded w-2/3" />
												</div>
											</div>
										))}
									</div>
								) : matchingReviews.length > 0 ? (
									<>
										<div className="space-y-2">
											{matchingReviews.map((review) => (
												<ReviewSelectItem
													key={review.id}
													review={review}
													isPinned={localPinnedIds.includes(review.id)}
													onToggleStar={() => (localPinnedIds.includes(review.id) ? handleUnpin(review.id) : handlePin(review.id))}
													disabled={isAtMaxPinned}
												/>
											))}
										</div>
										{nextCursor && (
											<div className="mt-4 text-center">
												<Button variant="soft" onClick={handleLoadMore} disabled={isLoadingMore}>
													{isLoadingMore ? "Loading..." : "Load More"}
												</Button>
											</div>
										)}
									</>
								) : (
									<div className="text-center py-12 text-tertiary">
										<svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={1.5}
												d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
											/>
										</svg>
										<p className="text-sm font-medium">No reviews match your filters.</p>
										<p className="text-sm">Try adjusting your filter criteria.</p>
									</div>
								)}
							</div>
						</div>

						{/* Right column: Pinned Reviews */}
						<div className="flex-1 basis-0 flex flex-col overflow-hidden">
							<div className="py-3 px-6 border-b border-gray-200 shrink-0">
								<div className="flex items-center justify-between">
									<div>
										<h3 className="font-semibold text-gray-900 flex gap-2 items-center">
											<IconPin /> {maxPinnedReviews != null ? `Pinned Reviews (${localPinnedIds.length}/${maxPinnedReviews})` : `Pinned Reviews (${localPinnedIds.length})`}
										</h3>
										<p className="text-sm text-tertiary mt-1">
											Drag to reorder. These always show on top of page.
										</p>
									</div>
								</div>
								{isAtMaxPinned && (
									<div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2.5 mt-3">
										<div className="flex items-center justify-between gap-2">
											<div className="flex items-start gap-2">
												<svg className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
													<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
												</svg>
												<div className="flex flex-col">
													<span className="text-sm font-semibold text-blue-800">Pinned limit reached</span>
													<span className="text-xs font-light text-blue-800">
														{userRole === "admin" ? "Upgrade to display more reviews." : "Contact your workspace admin to upgrade."}
													</span>
												</div>
											</div>
											{userRole === "admin" && (
												<button
													onClick={() => {
														onOpenChange(false);
														navigate(`/${workspaceId}/billing`);
													}}
													className="rounded-md bg-blue-600 hover:bg-blue-700 px-3 py-1.5 text-xs font-medium text-white transition-colors shrink-0"
												>
													Upgrade
												</button>
											)}
										</div>
									</div>
								)}
							</div>

							<div className="flex-1 p-6 overflow-y-auto">
								{localPinnedIds.length > 0 ? (
									<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
										<SortableContext items={localPinnedIds} strategy={verticalListSortingStrategy}>
											<div className="space-y-2">
												{pinnedReviews.map((review) => (
													<PinnedReviewItem key={review.id} review={review} onUnpin={() => handleUnpin(review.id)} draggable />
												))}
											</div>
										</SortableContext>
									</DndContext>
								) : (
									<div className="text-center py-12 text-tertiary">
										<svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={1.5}
												d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
											/>
										</svg>
										<p className="text-sm font-medium">No reviews pinned yet.</p>
										<p className="text-sm">Pin reviews to always show them on top.</p>
									</div>
								)}
							</div>
						</div>
					</div>

					{/* Footer */}
					<div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 shrink-0">
						<Button variant="soft" onClick={() => onOpenChange(false)}>
							Cancel
						</Button>
						<Button onClick={handleSave}>Done</Button>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
